import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Table,
  Button,
  Modal,
  message,
  Space,
  Tag,
  Card,
  Descriptions,
  Image,
  Empty,
  Form,
  Select,
  Alert,
} from 'antd';
import {
  ReloadOutlined,
  EyeOutlined,
  DownloadOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';
import dayjs from 'dayjs';

const MESES = [
  { value: 1, label: 'Enero' }, { value: 2, label: 'Febrero' }, { value: 3, label: 'Marzo' },
  { value: 4, label: 'Abril' }, { value: 5, label: 'Mayo' }, { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Septiembre' },
  { value: 10, label: 'Octubre' }, { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' },
];

const AlumnoHistorialPagos = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [registros, setRegistros] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalDetalleVisible, setModalDetalleVisible] = useState(false);
  const [modalNuevoPagoVisible, setModalNuevoPagoVisible] = useState(false);
  const [procesandoPago, setProcesandoPago] = useState(false);
  const [registroSeleccionado, setRegistroSeleccionado] = useState(null);
  const [formNuevoPago] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [configPagoAcademia, setConfigPagoAcademia] = useState({
    mercadoPagoActivo: false,
    mercadoPagoConfigurado: false,
  });
  const planSeleccionadoNuevoPago = Form.useWatch('planId', formNuevoPago);
  const planNuevoPago = planes.find(plan => Number(plan.pla_id) === Number(planSeleccionadoNuevoPago));

  useEffect(() => {
    loadPlanes();
    loadRegistros();
    loadConfigPagoAcademia();
  }, []);

  useEffect(() => {
    const mp = searchParams.get('mp');
    if (mp === 'success') {
      message.success('Pago enviado. Tu mensualidad se acreditará cuando Mercado Pago confirme el cobro.');
      setSearchParams({}, { replace: true });
      loadRegistros();
    } else if (mp === 'pending') {
      message.info('El pago está pendiente. Se acreditará cuando Mercado Pago lo apruebe.');
      setSearchParams({}, { replace: true });
      loadRegistros();
    } else if (mp === 'failure') {
      message.error('El pago no se completó. Podés intentar nuevamente.');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadPlanes = async () => {
    try {
      const response = await apiClient.get('/mi/planes');
      if (response.ok) {
        const data = await response.json();
        setPlanes((Array.isArray(data) ? data : []).filter(plan => plan.pla_activo));
      }
    } catch (error) {
      console.error('Error al cargar planes:', error);
    }
  };

  const loadConfigPagoAcademia = async () => {
    try {
      const response = await apiClient.get('/mi/registro-pagos/config-pago');
      if (response.ok) {
        const data = await response.json();
        setConfigPagoAcademia({
          mercadoPagoActivo: Boolean(data?.mercadoPagoActivo),
          mercadoPagoConfigurado: Boolean(data?.mercadoPagoConfigurado),
        });
      }
    } catch (error) {
      console.error('Error al cargar configuración de pago online:', error);
    }
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cargar registros de pago del alumno
  const loadRegistros = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/mi/registro-pagos');
      if (response.ok) {
        const data = await response.json();
        setRegistros(Array.isArray(data) ? data : []);
      } else {
        message.error('Error al cargar los registros de pago');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los registros de pago');
    } finally {
      setLoading(false);
    }
  };

  // Formatear mes/año para mostrar
  const formatMesAnio = (reg_mes, reg_anio) => {
    if (reg_mes != null && reg_anio != null) {
      const m = MESES.find(x => x.value === reg_mes);
      return m ? `${m.label} ${reg_anio}` : `${reg_mes}/${reg_anio}`;
    }
    return '-';
  };

  // Descargar comprobante
  const handleDownloadComprobante = (comprobanteBase64, tipoPago) => {
    try {
      if (!comprobanteBase64) {
        message.error('No hay comprobante disponible');
        return;
      }

      // Determinar tipo MIME y extensión
      let mimeType = 'application/octet-stream';
      let extension = 'bin';
      
      if (comprobanteBase64.startsWith('data:image/')) {
        if (comprobanteBase64.includes('data:image/jpeg') || comprobanteBase64.includes('data:image/jpg')) {
          mimeType = 'image/jpeg';
          extension = 'jpg';
        } else if (comprobanteBase64.includes('data:image/png')) {
          mimeType = 'image/png';
          extension = 'png';
        } else {
          mimeType = 'image/png';
          extension = 'png';
        }
      } else if (comprobanteBase64.startsWith('data:application/pdf')) {
        mimeType = 'application/pdf';
        extension = 'pdf';
      } else {
        // Si no tiene prefijo data:, intentar detectar por el contenido base64
        const base64Data = comprobanteBase64.split(',')[1] || comprobanteBase64;
        try {
          const decoded = atob(base64Data.substring(0, 20));
          if (decoded.startsWith('%PDF') || decoded.startsWith('JVBERi0')) {
            mimeType = 'application/pdf';
            extension = 'pdf';
          } else if (base64Data.startsWith('iVBORw0KG')) {
            mimeType = 'image/png';
            extension = 'png';
          } else if (base64Data.startsWith('/9j/')) {
            mimeType = 'image/jpeg';
            extension = 'jpg';
          }
        } catch (e) {
          // Si no se puede decodificar, usar valores por defecto
          console.warn('No se pudo detectar el tipo de archivo, usando valores por defecto');
        }
      }

      // Convertir base64 a blob
      const base64Data = comprobanteBase64.split(',')[1] || comprobanteBase64;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      // Crear URL y descargar
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `comprobante_${tipoPago || 'pago'}_${Date.now()}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar comprobante:', error);
      message.error('Error al descargar el comprobante');
    }
  };

  // Abrir modal de detalle
  const handleVerDetalle = (record) => {
    setRegistroSeleccionado(record);
    setModalDetalleVisible(true);
  };

  // Abrir modal para registrar pago mensual (nuevo)
  const handleAbrirNuevoPago = () => {
    formNuevoPago.resetFields();
    if (planes.length === 1) {
      formNuevoPago.setFieldsValue({ planId: planes[0].pla_id });
    }
    setModalNuevoPagoVisible(true);
  };

  // Crear pago mensual en Mercado Pago. El registro queda pendiente hasta el webhook.
  const handleSaveNuevoPago = async () => {
    if (procesandoPago) return;

    let redirigiendoMercadoPago = false;
    try {
      const values = await formNuevoPago.validateFields();
      if (!configPagoAcademia.mercadoPagoActivo) {
        message.warning('El pago online con Mercado Pago no está activo.');
        return;
      }
      if (!configPagoAcademia.mercadoPagoConfigurado) {
        message.warning('Mercado Pago no está configurado para Academia.');
        return;
      }

      const dataToSend = {
        planId: values.planId,
        mes: values.mes,
        anio: values.anio,
      };

      setProcesandoPago(true);
      const response = await apiClient.post('/mi/registro-pagos/mercadopago', dataToSend);
      const result = await response.json();
      if (response.ok) {
        if (result.init_point) {
          redirigiendoMercadoPago = true;
          message.loading('Redirigiendo a Mercado Pago...', 1);
          setTimeout(() => {
            window.location.href = result.init_point;
          }, 500);
          return;
        }
        message.error('No se recibió el link de pago de Mercado Pago');
      } else {
        message.error(result.message || 'Error al iniciar el pago');
      }
    } catch (error) {
      if (error.errorFields) return;
      message.error('Error al iniciar el pago');
    } finally {
      if (!redirigiendoMercadoPago) {
        setProcesandoPago(false);
      }
    }
  };

  // Formatear fecha
  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    return dayjs(fecha).format('DD/MM/YYYY HH:mm');
  };

  // Obtener color del estado
  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'PAGADO':
        return 'green';
      case 'PENDIENTE':
        return 'orange';
      case 'CANCELADO':
        return 'red';
      default:
        return 'default';
    }
  };

  // Columnas de la tabla
  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'reg_fecha_creacion',
      key: 'reg_fecha_creacion',
      render: (fecha) => formatFecha(fecha),
      width: isMobile ? 120 : undefined,
    },
    {
      title: 'Mes/Año',
      key: 'reg_mes_anio',
      render: (_, record) => formatMesAnio(record.reg_mes, record.reg_anio),
      width: isMobile ? 100 : undefined,
    },
    {
      title: 'Plan',
      dataIndex: 'plan_nombre',
      key: 'plan_nombre',
      render: (plan) => plan || '-',
      width: isMobile ? 120 : undefined,
    },
    {
      title: 'Total',
      dataIndex: 'reg_total',
      key: 'reg_total',
      render: (total) => `$${parseFloat(total).toFixed(2)}`,
      align: 'right',
      width: isMobile ? 100 : undefined,
    },
    {
      title: 'Método de Pago',
      dataIndex: 'tipo_pago_nombre',
      key: 'tipo_pago_nombre',
      render: (tipo) => tipo || '-',
      width: isMobile ? 120 : undefined,
    },
    {
      title: 'Estado',
      dataIndex: 'reg_estado_pago',
      key: 'reg_estado_pago',
      render: (estado) => (
        <Tag color={getEstadoColor(estado)}>
          {estado}
        </Tag>
      ),
      width: isMobile ? 100 : undefined,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: isMobile ? 150 : 200,
      fixed: isMobile ? 'right' : false,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleVerDetalle(record)}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? '' : 'Ver Detalle'}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? '16px' : '24px' }}>
        <div style={{ marginBottom: isMobile ? 12 : 16 }}>
          <h1 style={{ 
            margin: 0, 
            marginBottom: isMobile ? 12 : 16, 
            color: '#ffffff', 
            fontSize: isMobile ? '20px' : '24px' 
          }}>
            Mi Historial de Pagos
          </h1>
          <Space wrap style={{ marginBottom: isMobile ? 12 : 16 }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadRegistros}
              loading={loading}
              size={isMobile ? 'small' : 'middle'}
            >
              Actualizar
            </Button>
            {configPagoAcademia.mercadoPagoActivo && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAbrirNuevoPago}
                size={isMobile ? 'small' : 'middle'}
              >
                Registrar pago mensual
              </Button>
            )}
          </Space>
        </div>

        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {registros.length === 0 ? (
              <Empty description="No hay registros de pago" />
            ) : (
              registros.map(registro => (
                <Card
                  key={registro.reg_id}
                  hoverable
                  style={{
                    borderLeft: '4px solid #1890ff',
                    borderRadius: '8px',
                    background: '#2d2d2d',
                    borderColor: '#404040',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 'bold', fontSize: isMobile ? '15px' : '16px', marginBottom: '8px', color: '#ffffff' }}>
                          {formatMesAnio(registro.reg_mes, registro.reg_anio)}
                        </div>
                        <div style={{ fontSize: isMobile ? '12px' : '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                          Total: ${parseFloat(registro.reg_total).toFixed(2)}
                        </div>
                        <div style={{ fontSize: isMobile ? '12px' : '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                          Plan: {registro.plan_nombre || '-'}
                        </div>
                        <div style={{ fontSize: isMobile ? '12px' : '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                          Método: {registro.tipo_pago_nombre || '-'}
                        </div>
                        <div style={{ fontSize: isMobile ? '12px' : '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                          Fecha: {formatFecha(registro.reg_fecha_creacion)}
                        </div>
                      </div>
                      <Tag color={getEstadoColor(registro.reg_estado_pago)} style={{ flexShrink: 0 }}>
                        {registro.reg_estado_pago}
                      </Tag>
                    </div>
                    <Space wrap style={{ width: '100%' }}>
                      <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => handleVerDetalle(registro)}
                        style={{ color: '#1890ff', padding: isMobile ? '4px 8px' : undefined }}
                        size={isMobile ? 'small' : 'middle'}
                      >
                        Ver Detalle
                      </Button>
                    </Space>
                  </div>
                </Card>
              ))
            )}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={registros}
            loading={loading}
            rowKey="reg_id"
            pagination={false}
            scroll={{ x: isMobile ? 1000 : 'max-content' }}
            size={isMobile ? 'small' : 'middle'}
          />
        )}

        {/* Modal de detalle */}
        <Modal
          title="Detalle del Registro de Pago"
          open={modalDetalleVisible}
          onCancel={() => {
            setModalDetalleVisible(false);
            setRegistroSeleccionado(null);
          }}
          footer={[
            <Button key="close" onClick={() => {
              setModalDetalleVisible(false);
              setRegistroSeleccionado(null);
            }}>
              Cerrar
            </Button>
          ]}
          width={isMobile ? '95%' : 800}
        >
          {registroSeleccionado && (
            <div>
              <Descriptions
                title="Información del Pago"
                bordered
                column={isMobile ? 1 : 2}
                style={{ marginBottom: 24 }}
                size={isMobile ? 'small' : 'default'}
              >
                {(registroSeleccionado.reg_mes != null && registroSeleccionado.reg_anio != null) && (
                  <Descriptions.Item label="Mes/Año">
                    {formatMesAnio(registroSeleccionado.reg_mes, registroSeleccionado.reg_anio)}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Total">
                  ${parseFloat(registroSeleccionado.reg_total).toFixed(2)}
                </Descriptions.Item>
                <Descriptions.Item label="Plan">
                  {registroSeleccionado.plan_nombre || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Método de Pago">
                  {registroSeleccionado.tipo_pago_nombre || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Estado">
                  <Tag color={getEstadoColor(registroSeleccionado.reg_estado_pago)}>
                    {registroSeleccionado.reg_estado_pago}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Fecha de Creación">
                  {formatFecha(registroSeleccionado.reg_fecha_creacion)}
                </Descriptions.Item>
                {registroSeleccionado.reg_fecha_pago && (
                  <Descriptions.Item label="Fecha de Pago">
                    {formatFecha(registroSeleccionado.reg_fecha_pago)}
                  </Descriptions.Item>
                )}
                {registroSeleccionado.reg_comprobante && (
                  <Descriptions.Item label="Comprobante">
                    <div>
                      {registroSeleccionado.reg_comprobante.startsWith('data:image/') ? (
                        <Image
                          width={isMobile ? 150 : 200}
                          src={registroSeleccionado.reg_comprobante}
                          alt="Comprobante"
                          style={{ maxWidth: '100%', marginBottom: 8 }}
                        />
                      ) : (
                        <div style={{ 
                          padding: isMobile ? 12 : 16, 
                          backgroundColor: '#2d2d2d', 
                          borderRadius: 4,
                          marginBottom: 8,
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: isMobile ? 36 : 48, marginBottom: 8 }}>📄</div>
                          <div style={{ color: '#ffffff', fontSize: isMobile ? '13px' : '14px' }}>Archivo PDF</div>
                        </div>
                      )}
                      <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownloadComprobante(
                          registroSeleccionado.reg_comprobante,
                          registroSeleccionado.tipo_pago_nombre
                        )}
                        style={{ marginTop: 8 }}
                        size={isMobile ? 'small' : 'middle'}
                        block={isMobile}
                      >
                        Descargar Comprobante
                      </Button>
                    </div>
                  </Descriptions.Item>
                )}
              </Descriptions>
            </div>
          )}
        </Modal>

        {/* Modal Registrar pago mensual (nuevo) */}
        <Modal
          title="Registrar pago mensual"
          open={modalNuevoPagoVisible}
          onOk={handleSaveNuevoPago}
          onCancel={() => {
            if (procesandoPago) return;
            setModalNuevoPagoVisible(false);
            formNuevoPago.resetFields();
          }}
          okText="Pagar con Mercado Pago"
          cancelText="Cancelar"
          confirmLoading={procesandoPago}
          cancelButtonProps={{ disabled: procesandoPago }}
          width={isMobile ? '95%' : 520}
          okButtonProps={{
            disabled: procesandoPago || !configPagoAcademia.mercadoPagoActivo || !configPagoAcademia.mercadoPagoConfigurado,
          }}
        >
          {(!configPagoAcademia.mercadoPagoActivo || !configPagoAcademia.mercadoPagoConfigurado) && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                !configPagoAcademia.mercadoPagoActivo
                  ? 'El pago online con Mercado Pago no está activo.'
                  : 'Mercado Pago no está configurado para Academia.'
              }
            />
          )}

          <div style={{
            marginBottom: 16,
            padding: 12,
            background: '#162312',
            border: '1px solid #274916',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span style={{ color: '#8c8c8c', fontSize: 14 }}>Valor del plan:</span>
            <span style={{ color: '#52c41a', fontWeight: 700, fontSize: 18 }}>
              {planNuevoPago
                ? new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(planNuevoPago.pla_valor || 0))
                : 'Seleccione un plan'}
            </span>
          </div>

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Al confirmar, se abrirá Mercado Pago. El pago se registra cuando Mercado Pago avisa que fue aprobado."
          />

          <Form form={formNuevoPago} layout="vertical">
            <Form.Item
              name="planId"
              label="Plan"
              rules={[{ required: true, message: 'Seleccione el plan' }]}
            >
              <Select
                placeholder="Seleccione el plan"
                options={planes.map(plan => ({
                  value: plan.pla_id,
                  label: `${plan.pla_nombre} - ${new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(plan.pla_valor || 0))}`,
                }))}
              />
            </Form.Item>
            <Form.Item
              name="mes"
              label="Mes"
              rules={[{ required: true, message: 'Seleccione el mes' }]}
            >
              <Select
                placeholder="Mes"
                options={MESES.map(m => ({ value: m.value, label: m.label }))}
              />
            </Form.Item>
            <Form.Item
              name="anio"
              label="Año"
              rules={[{ required: true, message: 'Seleccione el año' }]}
            >
              <Select
                placeholder="Año"
                options={Array.from({ length: 5 }, (_, i) => new Date().getFullYear() + i).map(y => ({
                  value: y,
                  label: String(y),
                }))}
              />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default AlumnoHistorialPagos;

